<template>
    <div class="mb-[98px]">
        <section class="mt-[42px] grid-cols-layout pb-5 lg:grid">
            <div class="mb-6 lg:mb-0 lg:mt-4">
                <h3
                    class="mb-[5px] text-[15.75px] font-medium leading-[142%] tracking-[-0.01em] text-[#161E2E]"
                >
                    {{ t("vue.domain_seats_billing") }}
                </h3>
                <p
                    class="max-w-[333px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                >
                    {{ t("vue.domain_seats_billing_description") }}
                </p>
            </div>

            <div class="w-full">
                <DomainsOverview
                    label-class="min-h-[37px]"
                    :total-domains="domainSeatsBilling.totalDomains"
                    :total-active-domains="
                        domainSeatsBilling.totalActiveDomains
                    "
                    :total-inactive-domains="
                        domainSeatsBilling.totalInactiveDomains
                    "
                    :empty-domains-slots="domainSeatsBilling.emptyDomainsSlots"
                    :total-emails-reported="
                        domainSeatsBilling.totalEmailsReported
                    "
                    :total-parked-domains-count="
                        domainSeatsBilling.totalParkedDomainsCount
                    "
                    :total-emails-reported-count="
                        domainSeatsBilling.totalEmailsReportedCount
                    "
                    :total-domains-count="domainSeatsBilling.totalDomainsCount"
                />

                <form
                    class="mt-6 rounded-[12.8px] border border-gray-200 bg-white p-6"
                    @submit.prevent="submit"
                >
                    <div class="max-w-[680px]">
                        <h2 class="text-lg font-semibold text-slate-900">
                            {{ t("vue.next_period_paid_domain_seats") }}
                        </h2>
                        <p class="mt-2 text-sm leading-[142%] text-slate-600">
                            {{
                                t("vue.domain_seats_billing_paid_count_help", {
                                    count: domainSeatsBilling.paidDomainsCount,
                                    total: domainSeatsBilling.totalDomainsCount,
                                })
                            }}
                        </p>
                    </div>

                    <div class="mt-5 flex flex-col gap-3 sm:flex-row">
                        <label class="w-full sm:max-w-[220px]">
                            <span
                                class="mb-1 block text-sm font-medium text-slate-700"
                            >
                                {{ t("vue.paid_domain_seats") }}
                            </span>
                            <input
                                v-model.number="paidDomainsCount"
                                type="number"
                                min="1"
                                step="1"
                                class="w-full rounded-[6.75px] border border-gray-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-[#088bea] focus:outline-none focus:ring-1 focus:ring-[#088bea]"
                                @input="clearMessage"
                            />
                        </label>

                        <div class="flex items-end">
                            <button
                                type="submit"
                                class="inline-flex min-h-[38px] items-center justify-center rounded-[6.75px] bg-[#088bea] px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                                :disabled="isProcessing || isDebouncing"
                            >
                                {{
                                    isProcessing
                                        ? t("vue.processing")
                                        : t("vue.update_paid_domain_seats")
                                }}
                            </button>
                        </div>
                    </div>

                    <p
                        v-if="message"
                        class="mt-4 text-sm"
                        :class="
                            messageType === 'error'
                                ? 'text-red-600'
                                : messageType === 'success'
                                  ? 'text-emerald-600'
                                  : 'text-slate-600'
                        "
                    >
                        {{ message }}
                    </p>
                </form>
            </div>
        </section>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import DomainsOverview from "@/Pages/Domains/components/DomainsOverview.vue";
import { useTranslations } from "@/composables/useTranslations";

type DomainSeatsBillingData = {
    totalDomains: number;
    totalActiveDomains: number;
    totalInactiveDomains: number;
    emptyDomainsSlots: number;
    totalEmailsReported: number;
    totalParkedDomainsCount: number;
    totalEmailsReportedCount: number;
    totalDomainsCount: number;
    paidDomainsCount: number;
};

const props = defineProps<{
    domainSeatsBilling: DomainSeatsBillingData;
}>();

const { t } = useTranslations();

const paidDomainsCount = ref<number>(props.domainSeatsBilling.paidDomainsCount);
const isProcessing = ref<boolean>(false);
const isDebouncing = ref<boolean>(false);
const message = ref<string>("");
const messageType = ref<"info" | "success" | "error">("info");

const normalizedPaidDomainsCount = computed<number>(() => {
    const value = Number(paidDomainsCount.value);

    if (Number.isNaN(value) === true) {
        return 1;
    }

    return Math.max(1, Math.trunc(value));
});

const clearMessage = (): void => {
    message.value = "";
};

const startDebounce = (): void => {
    isDebouncing.value = true;

    window.setTimeout(() => {
        isDebouncing.value = false;
    }, 800);
};

const submit = (): void => {
    if (isProcessing.value === true || isDebouncing.value === true) {
        return;
    }

    startDebounce();

    if (
        normalizedPaidDomainsCount.value ===
        props.domainSeatsBilling.paidDomainsCount
    ) {
        messageType.value = "info";
        message.value = t("vue.domain_seats_billing_already_paid");
        return;
    }

    isProcessing.value = true;

    router.post(
        route("billing.subscriptions.domain-seats.update"),
        {
            paid_domains_count: normalizedPaidDomainsCount.value,
        },
        {
            preserveScroll: true,
            onSuccess: () => {
                messageType.value = "success";
                message.value = t("vue.domain_seats_billing_request_received");
            },
            onError: () => {
                messageType.value = "error";
                message.value = t("vue.domain_seats_billing_request_failed");
            },
            onFinish: () => {
                isProcessing.value = false;
            },
        },
    );
};
</script>
